import { useState } from 'react';
import { outbox } from '../lib/outbox';
import { getPosition } from '../lib/native';
import { DROP_LOCATIONS, FAIL_REASONS, type Task } from '../lib/types';
import { SwipeConfirm } from './SwipeConfirm';
import { PhotoPicker, type UploadedPhoto } from './PhotoPicker';
import { money } from '../lib/money';
import { Check, MessageCircle, X } from 'lucide-react';
import { InlineIcon } from './InlineIcon';

type Step = 'choose' | 'delivered' | 'failed';

/**
 * 完成本单。
 *
 * ★ 成功和失败是**同等权重的两个结果**,不是主按钮 + 次按钮。
 *   首次投递失败率行业平均 5–15%,把失败做成次要入口会导致司机
 *   找不到、或者干脆点「送达」了事 —— 后者是数据质量的灾难。
 * 见 docs/14-driver-ux-spec.md §14.6
 */
export function CompleteSheet({
  task,
  onClose,
  onDone,
  deliverOnly = false,
  confirmPrechecked = false,
}: {
  task: Task;
  onClose: () => void;
  onDone: () => Promise<void>;
  /**
   * 只显示「送达」——打开面板直接进送达那一步。
   *
   * ★ 「未送达」并没有消失,它挪到了面板底部一行小字。
   *   彻底去掉的话,送不到的那一单司机**只剩一个能按的东西**,
   *   而那个东西写着「送达」—— 他会按下去。
   *   假的送达比慢一步严重得多:货还在车上,而系统说送到了。
   */
  deliverOnly?: boolean;
  /** 代收、特殊物品这些确认项打开就是勾上的 */
  confirmPrechecked?: boolean;
}) {
  const [step, setStep] = useState<Step>(deliverOnly ? 'delivered' : 'choose');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  // 送达
  const [signedByType, setSignedByType] = useState<'SELF' | 'PROXY' | 'LEFT_AT_LOCATION'>('SELF');
  const [signedByName, setSignedByName] = useState('');
  const [dropLocation, setDropLocation] = useState<string>('DOOR');
  const [photos, setPhotos] = useState<UploadedPhoto[]>([]);

  /*
   * ★ 代收货款:**在这一步确认收到了**,而不是默认收了。
   *
   *   在这之前司机端根本没传过 `codCollected` —— 而后端的守卫要求它:
   *   带代收金额的单在司机端**根本送不掉**,报的是
   *   「该订单需代收货款,请先记录收款方式」,而界面上没有任何地方能记。
   *   (这一条是这次一并修的。)
   */
  /*
   * ★ 金额待定的单:司机这儿**不填金额**。
   *
   *   那个数要等结算算出来,他在门口也不知道 —— 逼他填一个,
   *   填进去的是他猜的,而那个猜的数会一路进对账。
   *   他能说的只有「钱收了、怎么收的」,金额由管理端事后补。
   */
  const codPending = task.codPending === true;
  const orderCod = Number(task.codAmount ?? 0);
  const needCod = orderCod > 0 || codPending;
  const codAmount = orderCod;
  const codAmountText = codPending ? '联系商家确认' : money(task.codAmount, task.currency);
  const [codDone, setCodDone] = useState(confirmPrechecked);
  /*
   * ★ 「他自己碰过没有」——默认勾上的那一勾**不是他勾的**。
   *
   *   勾上就变绿、不再闪的话,「这一单要收 120 块」这件事
   *   在屏幕上和「已经办妥」长得一模一样 —— 而他根本还没看。
   *   所以颜色和闪动跟着**碰没碰过**走,不跟着勾没勾走:
   *   他自己动过手才算看过,那时候才给他绿色。
   */
  const [codTouched, setCodTouched] = useState(false);
  const [codMethod, setCodMethod] = useState<'CASH' | 'TRANSFER'>('CASH');

  /*
   * ★ 特殊物品**逐项**确认。
   *   一个「都给了」的勾表示不了「冻品给了、易碎那件忘在车上」——
   *   而这类货的损失是当场发生、事后补不回来的。
   */
  const specialItems = task.specialItems ?? [];
  const [handedOver, setHandedOver] = useState<string[]>(
    confirmPrechecked ? (task.specialItems ?? []).map((i) => i.code) : [],
  );
  /** 同上:逐项记「这一项他自己碰过没有」 */
  const [itemsTouched, setItemsTouched] = useState<string[]>([]);

  // 失败
  const [failReason, setFailReason] = useState<string | null>(null);
  const [failDisposition, setFailDisposition] = useState<'RETRY' | 'RETURN_TO_SENDER'>('RETRY');
  const [failNote, setFailNote] = useState('');

  const proof = task.proof.delivery;
  // 「放置指定位置」强制拍照 —— 没人签收时照片是唯一证据,不受开关影响
  const photoRequired = proof.required || signedByType === 'LEFT_AT_LOCATION';
  const minPhotos = photoRequired ? Math.max(1, proof.minPhotos) : 0;

  /*
   * ★ 「还差什么」只算一次,而且要能说出**差的是哪一样**。
   *   一个灰按钮不告诉人原因,等于让他在原地试。
   */
  const missingItems = specialItems.filter((i) => !handedOver.includes(i.code));
  const ready = (!needCod || codDone) && missingItems.length === 0;
  const blockReason = !needCod || codDone
    ? missingItems.length > 0
      ? `还没确认交出:${missingItems.map((i) => i.label).join('、')}`
      : photos.length < minPhotos
        ? `还需 ${minPhotos - photos.length} 张照片`
        : null
    : codPending
      ? '先确认这一单的钱收到了'
      : `先确认收到 ${codAmountText}`;

  /**
   * 提交 —— 进本地队列,**不等网络**。
   *
   * 司机点完就能走,同步在后台做。冲突由 SyncBanner 统一提示。
   * idempotencyKey 由 outbox 生成(本地 ULID),重传不会产生重复事件。
   */
  async function submit(action: 'DELIVER' | 'FAIL') {
    if (busy) return;
    setBusy(true);
    setError(null);
    try {
      const geo = await getPosition();
      const slot = action === 'DELIVER' ? 'delivery' : 'fail';

      await outbox.enqueue({
        orderIds: [task.id],
        action,
        endpoint: `/orders/${task.id}/transitions`,
        body: {
          action,
          idempotencyKey: '',  // outbox 用自己的 id 覆盖
          ...(geo ? { geo } : {}),
          payload:
            action === 'DELIVER'
              ? {
                  signedByType,
                  ...(signedByType === 'PROXY' ? { signedByName } : {}),
                  ...(signedByType === 'LEFT_AT_LOCATION' ? { dropLocation } : {}),
                  // ★ 金额用**订单上的那个数**,不让司机手填 ——
                  //   手填的话,填错的那次没人发现,而账对不上是月底的事
                  /*
                   * ★ 待定的单只报**收款方式**,不报金额。
                   *   带一个 0 上去的话,后端会把它当成「这一单代收 0 元」,
                   *   而那正是这笔钱消失的方式。
                   */
                  ...(needCod
                    ? {
                        codCollected: codPending
                          ? { method: codMethod }
                          : { amount: codAmount, method: codMethod },
                      }
                    : {}),
                  ...(specialItems.length > 0 ? { specialItemsHandedOver: handedOver } : {}),
                }
              : {
                  failReason,
                  failDisposition,
                  ...(failNote ? { note: failNote } : {}),
                },
        },
        photos: photos.map((p) => ({
          slot,
          purpose: action === 'DELIVER' ? 'POD_PHOTO' : 'FAIL_PHOTO',
          blob: p.blob,
        })),
      });
      await onDone();
    } catch (e) {
      setError((e as Error).message || '提交失败,请重试');
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="fixed inset-0 z-50 flex items-end bg-black/50" onClick={onClose}>
      <div
        // 和其他弹层保持一致:给 role/aria-label,读屏才知道这是个独立层,
        // 也让「已送达」这类文案能被限定在面板范围内
        role="dialog"
        aria-modal="true"
        aria-label="完成本单"
        className="w-full max-h-[90dvh] overflow-y-auto rounded-t-2xl bg-white dark:bg-slate-900 pb-safe"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="sticky top-0 bg-white dark:bg-slate-900 px-4 pt-3 pb-2 border-b
                        border-slate-100 dark:border-slate-800">
          <div className="mx-auto mb-3 h-1 w-10 rounded-full bg-slate-300 dark:bg-slate-600" />
          <div className="flex items-center justify-between">
            <h3 className="text-lg font-semibold text-slate-900 dark:text-slate-100">
              {step === 'choose' ? '完成本单' : step === 'delivered' ? '确认送达' : '未送达'}
            </h3>
            <button onClick={onClose} aria-label="关闭" className="text-slate-400 text-xl px-2">
              <InlineIcon icon={X} />
            </button>
          </div>
          <p className="text-sm text-slate-500 mt-0.5">{task.receiverName} · {task.orderNo}</p>
        </div>

        <div className="p-4">
          {error && (
            <div className="mb-3 rounded-xl bg-red-50 dark:bg-red-950 border border-red-200
                            dark:border-red-900 px-4 py-3 text-red-700 dark:text-red-300">
              {error}
            </div>
          )}

          {/* ★ 二选一,等宽等高,同等视觉权重 */}
          {step === 'choose' && (
            <>
              <p className="mb-4 text-center text-slate-600 dark:text-slate-400">
                这一单的结果是?
              </p>
              <div className="grid grid-cols-2 gap-3">
                <button
                  onClick={() => setStep('delivered')}
                  className="h-32 rounded-2xl border-2 border-green-300 dark:border-green-800
                             bg-green-50 dark:bg-green-950/40 active:scale-[0.98] transition"
                >
                  <Check className="mx-auto mb-1 size-10 text-green-600 dark:text-green-400" />
                  <div className="text-lg font-medium text-green-800 dark:text-green-300">已送达</div>
                </button>
                <button
                  onClick={() => setStep('failed')}
                  className="h-32 rounded-2xl border-2 border-red-300 dark:border-red-800
                             bg-red-50 dark:bg-red-950/40 active:scale-[0.98] transition"
                >
                  <X className="mx-auto mb-1 size-10 text-red-600 dark:text-red-400" />
                  <div className="text-lg font-medium text-red-800 dark:text-red-300">未送达</div>
                </button>
              </div>
            </>
          )}

          {step === 'delivered' && (
            <div className="space-y-5">
              {/*
                ★ 这两块摆在**最上面**,在签收方式之前。
                  钱和货是「交出去之前」要做的事,签收方式是交出去时才填的 ——
                  顺序反了的话,他会先填完签收、滑动确认,才被拦下来。
              */}
              {needCod && (
                <Section label="这一单要收钱">
                  <label
                    className={`flex items-center gap-3 rounded-xl border-2 px-4 py-3
                      ${codDone && codTouched
                        ? 'border-green-500 bg-green-50 dark:bg-green-950/40'
                        : 'pulse-red border-red-400 bg-red-50 dark:bg-red-950/40'}`}
                  >
                    <input
                      type="checkbox"
                      aria-label={codPending ? '已收到代收货款' : `已收到 ${money(task.codAmount, task.currency)}`}
                      checked={codDone}
                      onChange={(e) => {
                        setCodTouched(true);
                        setCodDone(e.target.checked);
                      }}
                      className="h-6 w-6"
                    />
                    <span className="text-slate-900 dark:text-slate-100">
                      已收到{' '}
                      <span className="text-lg font-bold">{codAmountText}</span>
                    </span>
                  </label>

                  {/* 金额这一栏是空的 —— 说清楚由谁来补,免得他以为系统漏了 */}
                  {codPending && (
                    <div className="mt-1 text-xs text-slate-500 dark:text-slate-400">
                      这一单收多少要跟商家确认;金额由管理端后面补上,你不用填
                    </div>
                  )}

                  {codDone && (
                    <div className="mt-2 flex gap-2">
                      {(
                        [
                          ['CASH', '现金'],
                          ['TRANSFER', '转账'],
                        ] as const
                      ).map(([v, label]) => (
                        <button
                          key={v}
                          onClick={() => setCodMethod(v)}
                          className={`h-12 flex-1 rounded-xl border text-slate-900 dark:text-slate-100
                            ${codMethod === v
                              ? 'border-brand-500 bg-brand-50 dark:bg-brand-500/10'
                              : 'border-slate-200 dark:border-slate-700'}`}
                        >
                          {label}
                        </button>
                      ))}
                    </div>
                  )}

                  {/* ★ 没收到钱不该硬着头皮点送达 —— 告诉他正确的出路 */}
                  {!codDone && (
                    <p className="mt-2 text-sm text-red-600 dark:text-red-400">
                      钱没收到就别交货 —— 收不到的话,这一单选「未送达」并说明原因
                    </p>
                  )}
                </Section>
              )}

              {specialItems.length > 0 && (
                <Section label="这一单有特殊物品">
                  <div className="space-y-2">
                    {specialItems.map((it) => {
                      const on = handedOver.includes(it.code);
                      const touched = itemsTouched.includes(it.code);
                      return (
                        <label
                          key={it.code}
                          className={`flex items-center gap-3 rounded-xl border-2 px-4 py-3
                            ${on && touched
                              ? 'border-green-500 bg-green-50 dark:bg-green-950/40'
                              : 'pulse-amber border-amber-400 bg-amber-50 dark:bg-amber-950/40'}`}
                        >
                          <input
                            type="checkbox"
                            aria-label={`${it.label} 已交给收件人`}
                            checked={on}
                            onChange={(e) => {
                              setItemsTouched((cur) =>
                                cur.includes(it.code) ? cur : [...cur, it.code],
                              );
                              setHandedOver((cur) =>
                                e.target.checked
                                  ? [...cur, it.code]
                                  : cur.filter((c) => c !== it.code),
                              );
                            }}
                            className="h-6 w-6"
                          />
                          <span className="text-slate-900 dark:text-slate-100">
                            {it.icon ? `${it.icon} ` : ''}
                            <span className="font-medium">{it.label}</span> 已经交给收件人
                          </span>
                        </label>
                      );
                    })}
                  </div>
                </Section>
              )}

              <Section label="签收方式">
                <div className="space-y-2">
                  {(
                    [
                      ['SELF', '本人签收'],
                      ['PROXY', '他人代收'],
                      ['LEFT_AT_LOCATION', '放在指定位置'],
                    ] as const
                  ).map(([v, label]) => (
                    <label
                      key={v}
                      className={`flex items-center gap-3 h-13 px-4 py-3 rounded-xl border cursor-pointer
                        ${signedByType === v
                          ? 'border-brand-500 bg-brand-50 dark:bg-brand-500/10'
                          : 'border-slate-200 dark:border-slate-700'}`}
                    >
                      <input
                        type="radio"
                        checked={signedByType === v}
                        onChange={() => setSignedByType(v)}
                        className="w-5 h-5"
                      />
                      <span className="text-slate-900 dark:text-slate-100">{label}</span>
                      {v === 'LEFT_AT_LOCATION' && (
                        <span className="ml-auto text-xs text-amber-600 dark:text-amber-400">
                          须拍照
                        </span>
                      )}
                    </label>
                  ))}
                </div>

                {signedByType === 'PROXY' && (
                  <input
                    value={signedByName}
                    onChange={(e) => setSignedByName(e.target.value)}
                    placeholder="代收人姓名"
                    className="mt-2 w-full h-13 px-4 rounded-xl border border-slate-300
                               dark:border-slate-700 bg-white dark:bg-slate-800
                               text-slate-900 dark:text-slate-100"
                  />
                )}

                {signedByType === 'LEFT_AT_LOCATION' && (
                  <div className="mt-2 flex flex-wrap gap-2">
                    {DROP_LOCATIONS.map((d) => (
                      <button
                        key={d.code}
                        onClick={() => setDropLocation(d.code)}
                        className={`px-4 py-2.5 rounded-xl border text-slate-900 dark:text-slate-100
                          ${dropLocation === d.code
                            ? 'border-brand-500 bg-brand-50 dark:bg-brand-500/10'
                            : 'border-slate-200 dark:border-slate-700'}`}
                      >
                        {d.label}
                      </button>
                    ))}
                  </div>
                )}
              </Section>

              {/* 拍照关闭时整块不显示,而不是显示一个"可跳过"的区域 */}
              {photoRequired && (
                <Section label={`送达照片(必填,最多 ${proof.maxPhotos} 张)`}>
                  {proof.hint && (
                    <p className="mb-2 text-sm text-slate-500"><InlineIcon icon={MessageCircle} /> {proof.hint}</p>
                  )}
                  <PhotoPicker
                    purpose="POD_PHOTO"
                    max={proof.maxPhotos}
                    photos={photos}
                    onChange={setPhotos}
                  />
                </Section>
              )}

              {!photoRequired && (
                <details className="text-sm">
                  <summary className="text-slate-500 py-2">▸ 添加照片(选填)</summary>
                  <PhotoPicker purpose="POD_PHOTO" max={3} photos={photos} onChange={setPhotos} />
                </details>
              )}

              {/* 终态不可撤销 → 滑动确认防误触 */}
              {/*
                ★ 拦在**滑动确认**这一步,而不是等后端拒。
                  后端确实也会拒(守卫),但司机端是离线优先的:
                  提交进的是本地队列,他滑完就走人了,
                  错误要过几分钟同步时才浮出来 —— 那时候他已经在下一单了。
              */}
              <SwipeConfirm
                label="滑动确认送达"
                disabled={busy || photos.length < minPhotos || !ready}
                hint={blockReason ?? undefined}
                onConfirm={() => void submit('DELIVER')}
              />

              {/*
                ★ 「只显示送达」开着时,送不到的路仍然要留一条。
                  一条小字,不抢视线 —— 但**必须存在**:
                  送不到的那一单如果只剩一个写着「送达」的东西能按,
                  司机就会按下去。假的送达比慢一步严重得多:
                  货还在车上,而系统说送到了,店家、收件人、客服都被骗一遍。
              */}
              {deliverOnly && (
                <button
                  onClick={() => setStep('failed')}
                  className="w-full py-2 text-sm text-slate-500 underline underline-offset-4
                             dark:text-slate-400"
                >
                  送不到?改成「未送达」
                </button>
              )}
            </div>
          )}

          {step === 'failed' && (
            <div className="space-y-5">
              <Section label="为什么没送到?">
                {/* 预置选项 + 图标。司机在门口单手操作,不能让他打字 */}
                <div className="space-y-2">
                  {FAIL_REASONS.map((r) => (
                    <button
                      key={r.code}
                      onClick={() => setFailReason(r.code)}
                      className={`w-full flex items-center gap-3 px-4 py-3.5 rounded-xl border text-left
                        ${failReason === r.code
                          ? 'border-brand-500 bg-brand-50 dark:bg-brand-500/10'
                          : 'border-slate-200 dark:border-slate-700'}`}
                    >
                      <InlineIcon icon={r.icon} className="text-xl" />
                      <span className="text-slate-900 dark:text-slate-100">{r.label}</span>
                    </button>
                  ))}
                </div>
              </Section>

              {/* ★ 必答:只记原因不问货怎么处理,会导致「货在司机手上但没人知道」 */}
              <Section label="接下来怎么办?">
                <div className="grid grid-cols-2 gap-3">
                  {(
                    [
                      ['RETRY', '稍后再试一次'],
                      ['RETURN_TO_SENDER', '退回店里'],
                    ] as const
                  ).map(([v, label]) => (
                    <button
                      key={v}
                      onClick={() => setFailDisposition(v)}
                      className={`h-14 rounded-xl border text-slate-900 dark:text-slate-100
                        ${failDisposition === v
                          ? 'border-brand-500 bg-brand-50 dark:bg-brand-500/10'
                          : 'border-slate-200 dark:border-slate-700'}`}
                    >
                      {label}
                    </button>
                  ))}
                </div>
              </Section>

              <Section label="现场照片(建议)">
                <PhotoPicker purpose="FAIL_PHOTO" max={3} photos={photos} onChange={setPhotos} />
              </Section>

              <input
                value={failNote}
                onChange={(e) => setFailNote(e.target.value)}
                placeholder="补充说明(选填)"
                className="w-full h-13 px-4 rounded-xl border border-slate-300 dark:border-slate-700
                           bg-white dark:bg-slate-800 text-slate-900 dark:text-slate-100"
              />

              <button
                onClick={() => void submit('FAIL')}
                disabled={busy || !failReason}
                className="w-full h-14 rounded-xl bg-red-600 text-white text-lg font-medium
                           disabled:opacity-40 active:bg-red-700 transition"
              >
                {busy ? '提交中…' : !failReason ? '请先选择原因' : '提交'}
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function Section({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div>
      <h4 className="mb-2 text-sm font-medium text-slate-600 dark:text-slate-400">{label}</h4>
      {children}
    </div>
  );
}

